import { useEffect } from "react";

/**
 * Lightweight per-route SEO manager.
 * Sets the document title + updates description, canonical, and Open Graph /
 * Twitter card meta tags. Falls back to a shared brand baseline set in index.html.
 *
 * Usage: useSeo({ title, description, path, image })
 */
const BASE_URL = "https://swiftca.sh";
const BASE_IMAGE = "https://swiftca.sh/og-image.png";

function upsertMeta(selector, attr, name, content) {
  if (!content) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

// Organization + WebSite JSON-LD baseline (present on every page)
const BASE_JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": "https://swiftca.sh/#org",
      "name": "SwiftCash",
      "url": "https://swiftca.sh",
      "logo": "https://swiftca.sh/favicon.svg",
      "sameAs": [],
      "description": "UK business finance from £5,000 to £500,000,000. Whole-of-market panel.",
      "parentOrganization": { "@type": "Organization", "name": "Connexis Group Ltd" },
    },
    {
      "@type": "FinancialProduct",
      "@id": "https://swiftca.sh/#product",
      "name": "SwiftCash Business Finance",
      "provider": { "@id": "https://swiftca.sh/#org" },
      "areaServed": { "@type": "Country", "name": "United Kingdom" },
      "feesAndCommissionsSpecification": "No listing or monthly fees. Success fee on won deals only.",
      "annualPercentageRate": { "@type": "QuantitativeValue", "minValue": 5.9, "unitText": "APR" },
      "amount": { "@type": "MonetaryAmount", "currency": "GBP", "minValue": 5000, "maxValue": 500000000 },
    },
    {
      "@type": "WebSite",
      "@id": "https://swiftca.sh/#site",
      "url": "https://swiftca.sh",
      "name": "SwiftCash",
      "publisher": { "@id": "https://swiftca.sh/#org" },
    },
  ],
};

function upsertJsonLd(id, data) {
  if (!data) return;
  let el = document.head.querySelector(`script[data-ldid="${id}"]`);
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-ldid", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export default function useSeo({ title, description, path = "/", image = BASE_IMAGE, type = "website", jsonLd = null }) {
  useEffect(() => {
    const url = `${BASE_URL}${path}`;
    if (title) document.title = title;

    upsertMeta('meta[name="description"]', "name", "description", description);
    upsertMeta('meta[property="og:title"]', "property", "og:title", title);
    upsertMeta('meta[property="og:description"]', "property", "og:description", description);
    upsertMeta('meta[property="og:url"]', "property", "og:url", url);
    upsertMeta('meta[property="og:image"]', "property", "og:image", image);
    upsertMeta('meta[property="og:type"]', "property", "og:type", type);
    upsertMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    upsertMeta('meta[name="twitter:image"]', "name", "twitter:image", image);
    upsertLink("canonical", url);

    // JSON-LD structured data — always inject the base graph + optional per-page schema
    upsertJsonLd("base", BASE_JSON_LD);
    upsertJsonLd("page", jsonLd);

    const gsc = process.env.REACT_APP_GSC_VERIFICATION;
    if (gsc) upsertMeta('meta[name="google-site-verification"]', "name", "google-site-verification", gsc);
    const bing = process.env.REACT_APP_BING_VERIFICATION;
    if (bing) upsertMeta('meta[name="msvalidate.01"]', "name", "msvalidate.01", bing);
    const yandex = process.env.REACT_APP_YANDEX_VERIFICATION;
    if (yandex) upsertMeta('meta[name="yandex-verification"]', "name", "yandex-verification", yandex);
  }, [title, description, path, image, type, jsonLd]);
}
